import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NgbDropdownModule, NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { SortHeaderComponent } from '../../../../../_shared/components/sort-header/sort-header.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { PartsUsageService } from '../../services/parts-usage.service';
import { PartsUsageExportRequest, PartsUsageFilterRequest, PartsUsageItem, PartsUsageItemStatus, PartsUsageSortColumn, PartsUsageStatus, SortDirection } from '../../models/parts-usage.model';
import { ViewPartNotesComponent } from '../modals/view-part-notes/view-part-notes.component';
import { ExportComponent } from '../../../../../_shared/components/export/export/export.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-parts-usage-overview',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchComponent, EmptyStateComponent, SortHeaderComponent, ErrorStateComponent, PaginationComponent, NgbDropdownModule, NgbTooltipModule, ViewPartNotesComponent, ExportComponent, TranslocoDirective],
  templateUrl: './parts-usage-overview.component.html',
  styleUrl: './parts-usage-overview.component.scss'
})
export class PartsUsageOverviewComponent implements OnInit, OnDestroy {
  public PartsUsageStatus = PartsUsageStatus;
  public PartsUsageItemStatus = PartsUsageItemStatus;
  public hasError: boolean = false;

  private readonly pageSize: number = 6;
  private readonly userTooltipThreshold = 16;
  private readonly partNumberTooltipThreshold = 14;
  private readonly partNameTooltipThreshold = 20;
  private readonly categoryTooltipThreshold = 14;

  private partsUsageService = inject(PartsUsageService);
  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private modalService = inject(NgbModal);
  private transloco = inject(TranslocoService);

  searchTerm: string = '';
  selectedStatus: PartsUsageStatus = PartsUsageStatus.All;
  isLoading: boolean = false;
  sortColumn: PartsUsageSortColumn | null = null;
  sortDirection: SortDirection = null;

  filteredItems: PartsUsageItem[] = [];

  currentPage: number = 1;
  totalItems: number = 0;
  totalPages: number = 0;

  statusOptions: PartsUsageStatus[] = [
    PartsUsageStatus.All,
    PartsUsageStatus.Issued,
    PartsUsageStatus.Returned
  ];

  private widgetFilter: WidgetFilterData = {} as WidgetFilterData;
  private loadCancellation: Subject<void> = new Subject<void>();
  private destroySubject: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(value => value === null)) {
      void this.resetAndLoadAsync();
    }

    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((filter) => {
        if (!filter || Object.values(filter).every(value => value === null)) {
          return;
        }

        this.widgetFilter = filter;
        void this.resetAndLoadAsync();
      });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    void this.resetAndLoadAsync();
  }

  onStatusFilterChange(status: PartsUsageStatus): void {
    this.selectedStatus = status;
    void this.resetAndLoadAsync();
  }

  onSort(column: string): void {
    const nextDirection: SortDirection =
      this.sortColumn !== column || !this.sortDirection
        ? 'asc'
        : this.sortDirection === 'asc'
          ? 'desc'
          : null;

    this.sortColumn = nextDirection ? (column as PartsUsageSortColumn) : null;
    this.sortDirection = nextDirection;
    void this.resetAndLoadAsync();
  }

  async onPageChange(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    await this.loadPartsUsageAsync();
  }

  getStatusClass(status: PartsUsageItemStatus): string {
    return status === PartsUsageItemStatus.Issued ? 'status-issued' : 'status-returned';
  }

  getStatusLabel(status: PartsUsageStatus): string {
    switch (status) {
      case PartsUsageStatus.Issued:
        return this.transloco.translate('parts.usage.filters.issued');
      case PartsUsageStatus.Returned:
        return this.transloco.translate('parts.usage.filters.returned');
      default:
        return this.transloco.translate('parts.usage.filters.all');
    }
  }

  getItemStatusLabel(status: PartsUsageItemStatus): string {
    return status === PartsUsageItemStatus.Returned
      ? this.transloco.translate('parts.usage.status.returned')
      : this.transloco.translate('parts.usage.status.issued');
  }

  onNotesClick(item: PartsUsageItem, event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.modalService.open(ViewPartNotesComponent, {
      windowClass: 'view-part-notes-modal-window',
      backdrop: 'static',
      centered: true
    });
    ref.componentInstance.part = { id: item.requestId, partNumber: item.partNumber, category: item.category };
    ref.componentInstance.workOrderNumber = item.workOrderNumber;
    ref.result.then(() => {
      item.hasNotes = true;
    }).catch(() => {});
  }

  getUserTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.userTooltipThreshold) ? value! : null;
  }

  getPartNumberTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.partNumberTooltipThreshold) ? value! : null;
  }

  getPartNameTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.partNameTooltipThreshold) ? value! : null;
  }

  getCategoryTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.categoryTooltipThreshold) ? value! : null;
  }

  getDescriptionText(value: string | null | undefined): string {
    return value?.trim() ? value : this.transloco.translate('parts.usage.notAvailable');
  }

  getExportRequest(): PartsUsageExportRequest {
    return this.buildFilterRequest();
  }

  getShowingText(): string {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return this.transloco.translate('common.showingOutOf', { current: start, total: this.totalItems });
  }

  private async resetAndLoadAsync(): Promise<void> {
    this.currentPage = 1;
    this.filteredItems = [];
    await this.loadPartsUsageAsync();
  }

  private async loadPartsUsageAsync(): Promise<void> {
    this.loadCancellation.next();

    this.isLoading = true;
    this.hasError = false;

    try {
      const request: PartsUsageFilterRequest = {
        ...this.buildFilterRequest(),
        page: this.currentPage,
        pageSize: this.pageSize,
        sortColumn: this.sortColumn ?? undefined,
        sortDirection: this.sortDirection ?? undefined
      };

      const response = await this.partsUsageService.getPartsUsageAsync(request, this.loadCancellation);

      this.filteredItems = response.data?.results ?? [];
      this.totalItems = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private buildFilterRequest(): PartsUsageExportRequest {
    return {
      searchTerm: this.searchTerm.trim(),
      accountIds: this.widgetFilter.accountIds ?? [],
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
      interval: this.widgetFilter.interval,
      status: this.selectedStatus !== PartsUsageStatus.All ? this.selectedStatus : null
    };
  }

  private shouldShowTooltip(value: string | null | undefined, threshold: number): boolean {
    return !!value && value.trim().length > threshold;
  }
}